import { ActionCardModel, isConditionSatisfied, ViewMode } from '@playbooks/action-cards-utils'
import { ActionCard, Section } from '@playbooks/core/src/interfaces/ActionCard'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  MultilevelAccordionContent,
  MultilevelAccordionItem,
  MultilevelAccordionTrigger,
} from './ui/MultilevelAccordion'
import { cn } from '@playbooks/ui/lib/utils'
import LockUnlockButton from './LockUnlockButton'
import { CircleCheck, Minus, Plus } from 'lucide-react'
import Score from './Score'
import { useAppContext } from '../contexts/AppContext'
import Sections from './Sections'
import useHtmlWithPlateTwStylesIsolated from '../hooks/useHtmlWithPlateTwStylesIsolated'
import scrollIntoView from '../utils/scrollIntoView'
import { AnyUpdateTuple } from '../store/actions/actionUpdate'
import useMountEffect from '../hooks/useMountEffect'
import ScrollDownButton from './ScrollDownButton'
import scrollABit from '../utils/scrollABit'
import { sectionTypes } from '@playbooks/core/src/constants'

export default function Separator({
  isHidden,
  data,
  sections,
  id,
}: {
  id: number
  isHidden: boolean
  data: Section
  sections: Section[]
}) {
  const {
    onTouch,
    settings: { viewMode, hideLock, hideScore, headers: displayHeadersCount, isSplitViewEnabled },
    actions,
    data: state,
    update,
  } = useAppContext()
  const [isSeparatorInView, setIsSeparatorInView] = useState(false)
  const [isScrollButtonVisible, setIsScrollButtonVisible] = useState(false)
  const separatorRef = useRef<HTMLDivElement>(null)
  const placeholderRef = useRef<HTMLDivElement>(null)

  useHtmlWithPlateTwStylesIsolated(placeholderRef, data.placeholder || '')

  const sectionsToDisplay = sections.filter(section => {
    if (!isSplitViewEnabled) return true

    return !section.isPinned
  })

  const showPlaceholder = sectionsToDisplay.length === 0

  const isFunnelView = viewMode === ViewMode.FUNNEL
  const isDocView = viewMode === ViewMode.DOC

  const enabled = isSeparatorEnabled(displayHeadersCount, isFunnelView, state, data)

  const handleLockClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    update(data, 'isLocked', !data.isLocked)
  }

  const handleSeparatorClick = () => {
    // In funnel view, separators are not collapsible, so we do nothing on click
    if (isFunnelView) return

    const scrollContainer = separatorRef.current?.closest<HTMLDivElement>(
      '[data-role="data-action-cards-sides-scroll-container"]',
    )

    if (!data.isOpened && separatorRef.current && scrollContainer) {
      scrollIntoView({
        scrollIntoViewElement: separatorRef.current,
        scrollContainerRef: scrollContainer,
      })
    }

    if (data.collapsePreviousHeader && !data.isOpened) {
      const separatorsBeforeCurrent = []

      for (const separator of state.sections) {
        if (separator.type !== sectionTypes.SEPARATOR) continue
        if (separator.id === data.id) break

        separatorsBeforeCurrent.push(separator)
      }
      const updateData = separatorsBeforeCurrent.map(separator => [separator, 'isOpened', false])

      update(
        (updateData.length
          ? [...updateData, [data, 'isOpened', !data.isOpened]]
          : [[data, 'isOpened', !data.isOpened]]) as AnyUpdateTuple[],
      )
    } else {
      update(data, 'isOpened', !data.isOpened)
    }
  }

  const handleTouch = (e: React.MouseEvent<HTMLDivElement>) => {
    onTouch?.({ lastTouchedHeader: data })

    // SetTimeout is needed to optimistically handle scroll after as the container change its scroll height
    // Because some element cab be conditionally hidden and appear in the interaction moment
    setTimeout(() => {
      if (!separatorRef.current) return
      const contentElement = separatorRef.current.querySelector('[data-role="multilevel-accordion-content"]')!

      // Additional separator scroll if user interacts whit bottom part of the slide
      scrollABit(separatorRef.current, contentElement, e.clientY)
    }, 100)

    if (isFunnelView) {
      e.stopPropagation()
    }
  }

  const handleClick = () => {
    if (separatorRef.current) {
      separatorRef.current.scrollBy({ behavior: 'smooth', top: 300 })
    }
  }

  /**
   * Check if the separator can switch to the next slide automatically
   */
  const canSwitchToNextSlide = useCallback(
    (data: Section, actionCardSource: ActionCardModel | ActionCard) => {
      if (!isSeparatorInView) return false
      if (!data.tags?.length) return false
      if (viewMode !== ViewMode.FUNNEL) return false

      return isConditionSatisfied(data.tags || [], actionCardSource.conditionalItems || [], data.conditionRule || null)
    },
    [isSeparatorInView, viewMode],
  )

  const hasBeenSwitchedRef = useRef(false)

  // Automatically switch to the next slide handling
  useEffect(() => {
    if (!canSwitchToNextSlide(data, state) || hasBeenSwitchedRef.current) return

    hasBeenSwitchedRef.current = true
    actions?.switchToNextSlideAutomatically?.()

    // We should rely on state.conditionalItems change only to avoid unnecessary action calls
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(state.conditionalItems), isSeparatorInView])

  return (
    <MultilevelAccordionItem
      ref={separatorRef}
      className={cn(
        'border-color-custom-border/50 shrink-0 w-full',

        'group-data-[view=default]/app:border-b',

        'group-data-[view=funnel]/app:overflow-auto',
        'group-data-[view=funnel]/app:h-full',
        'group-data-[view=funnel]/app:w-(--slide-width)',
        data.isOpened && 'last:border-transparent',
      )}
      data-separator-scope={id}
      data-funnel={isFunnelView}
      data-type="Separator"
      data-role="slide"
      data-chevron={!isFunnelView}
      open={isFunnelView || isHidden || !!data.isOpened}
      renderFooter={() => {
        return isScrollButtonVisible && isFunnelView ? (
          <div className="sticky flex flex-row-reverse bottom-24 h-0 w-full pointer-events-none translate-x-0">
            <div className="fixed right-0 pointer-events-auto">
              <ScrollDownButton handleClick={handleClick} />
            </div>
          </div>
        ) : null
      }}
      renderTrigger={({ isOpen }) => (
        <MultilevelAccordionTrigger
          isOpen={isOpen}
          hideChevron={isFunnelView}
          chevron={isDocView ? isOpen ? <Minus /> : <Plus /> : undefined}
          onClick={handleSeparatorClick}
          className={cn(
            'sticky top-0 z-20',
            'group/separator px-2',
            'border-color-custom-border/50',

            'group-data-[view=funnel]/app:min-h-auto',
            'group-data-[view=funnel]/app:py-5',
            'group-data-[view=funnel]/app:border-b',
            'group-data-[view=funnel]/app:border-b',

            'group-data-[view=doc]/app:px-4',
            'group-data-[view=doc]/app:pr-5',
            'group-data-[view=doc]/app:flex-row-reverse',
            'group-data-[view=doc]/app:border-b',

            !isFunnelView && 'cursor-pointer',
            isHidden && 'hidden',
          )}
        >
          <div
            className={cn(
              'text-base font-semibold leading-6 mr-auto pl-2 ',

              'group-data-[view=doc]/app:order-1',

              'group-data-[view=funnel]/app:text-3xl',
              'group-data-[view=funnel]/app:leading-9',
            )}
          >
            {!isHidden && data.name}
          </div>
          <div className="flex items-center gap-2">
            {!hideLock && !isDocView && (
              <div
                className={cn(
                  '-mr-2.5',
                  'group-hover/separator:block!',
                  'data-locked:block data-[locked=false]:hidden',

                  'group-data-[view=funnel]/app:hidden',
                )}
                data-locked={!!data.isLocked}
              >
                <LockUnlockButton isLocked={!!data.isLocked} onClick={handleLockClick} />
              </div>
            )}
            {enabled && <CircleCheck size={16} />}
            <div className="group-data-[view=doc]/app:mr-2 relative -top-px group-data-[view=funnel]/app:pr-2">
              <Score value={data.score} hidden={hideScore} />
            </div>
          </div>
        </MultilevelAccordionTrigger>
      )}
    >
      <MultilevelAccordionContent
        className="relative group-data-[view=funnel]/app:px-0 px-2 translate-x-0"
        onClick={handleTouch}
      >
        <ComponentInViewDetector
          setIsSeparatorInViewVisible={setIsSeparatorInView}
          className="absolute left-1 top-0 bottom-5 w-px h-full pointer-events-none"
        />
        <div
          className={cn(
            'overflow-auto isolated-html-content group-data-[view=funnel]/app:p-4 group-data-[view=doc]/app:p-4',
            showPlaceholder ? 'block' : 'hidden',
          )}
          ref={placeholderRef}
        />
        <Sections sections={sectionsToDisplay} />
        <ComponentInViewDetector
          setIsSeparatorInViewVisible={value => setIsScrollButtonVisible(!value)}
          className="sticky left-0 bottom-5 h-4 w-full pointer-events-none"
        />
      </MultilevelAccordionContent>
    </MultilevelAccordionItem>
  )
}

function isSeparatorEnabled(
  displayHeadersCount: number | null | undefined,
  isFunnelView: boolean,
  state: ActionCard,
  data: Section,
) {
  // Special mode (#11665)
  const enableScoreFlagForSpecialMode = displayHeadersCount === null ? false : displayHeadersCount! <= 1 && isFunnelView
  const enabled = state.enabledSeparators?.some(
    sepId => (sepId === data.id && !isFunnelView) || enableScoreFlagForSpecialMode,
  )
  return enabled
}

/**
 * Intersection element to detect a component locates in view
 */
function ComponentInViewDetector({
  setIsSeparatorInViewVisible,
  className,
}: {
  className?: string
  setIsSeparatorInViewVisible?: (visible: boolean) => void
}) {
  const dotRef = useRef<HTMLDivElement>(null)

  useMountEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (setIsSeparatorInViewVisible) {
            setIsSeparatorInViewVisible(entry.isIntersecting)
          }
        })
      },
      {
        root: null,
        threshold: 0,
      },
    )

    const element = dotRef.current

    if (element) {
      observer.observe(element)
    }

    return () => {
      if (element) {
        observer.unobserve(element)
      }
    }
  })
  return <div ref={dotRef} className={cn(className)} />
}
